/**
 * CoordinateSystemManager - Coordinate Reference System management service
 *
 * Provides CRS information, layer reprojection, and coordinate transformation
 * using Proj4js for projection definitions and transformations.
 *
 * Validates: Requirements 44.1, 44.2, 44.3, 44.4, 45.1, 45.2, 45.3, 45.4, 45.5, 46.1, 46.2, 46.3, 46.4, 46.5
 */
import proj4 from 'proj4';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CRSInfo {
  epsgCode: string;
  name: string;
  projectionType: string;
  units: string;
  applicableArea: string;
  accuracy: string;
}

export type Coordinate = [number, number, ...number[]];

export interface TransformResult {
  coordinates: Coordinate;
  sourceCRS: string;
  targetCRS: string;
}

export interface BatchTransformResult {
  coordinates: Coordinate[];
  sourceCRS: string;
  targetCRS: string;
  count: number;
}

export interface ReprojectedFeature {
  id: string;
  geometry: GeoJSON.Geometry;
  properties: Record<string, any>;
}

export interface ReprojectedLayer {
  name: string;
  crs: string;
  geometryType: string;
  features: ReprojectedFeature[];
}

export interface TransformAccuracyInfo {
  sourceCRS: string;
  targetCRS: string;
  estimatedAccuracy: string;
  applicableArea: string;
  notes: string;
}

// ─── CRS Definitions Registry ────────────────────────────────────────────────

/**
 * Common CRS definitions with their Proj4 strings and metadata.
 * Proj4js already knows EPSG:4326 and EPSG:3857 by default,
 * but we register them explicitly for consistency.
 */
const CRS_DEFINITIONS: Record<string, { proj4: string; info: CRSInfo }> = {
  'EPSG:4326': {
    proj4: '+proj=longlat +datum=WGS84 +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:4326',
      name: 'WGS 84',
      projectionType: 'Geographic',
      units: 'degrees',
      applicableArea: 'World',
      accuracy: '±1m (with precise coordinates)',
    },
  },
  'EPSG:3857': {
    proj4:
      '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:3857',
      name: 'WGS 84 / Pseudo-Mercator',
      projectionType: 'Projected (Mercator)',
      units: 'meters',
      applicableArea: 'World between 85.06°S and 85.06°N',
      accuracy: '±1m (at equator, degrades toward poles)',
    },
  },
  'EPSG:32632': {
    proj4: '+proj=utm +zone=32 +datum=WGS84 +units=m +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:32632',
      name: 'WGS 84 / UTM zone 32N',
      projectionType: 'Projected (Transverse Mercator)',
      units: 'meters',
      applicableArea: 'Northern hemisphere between 6°E and 12°E',
      accuracy: '±0.5m (within zone)',
    },
  },
  'EPSG:32633': {
    proj4: '+proj=utm +zone=33 +datum=WGS84 +units=m +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:32633',
      name: 'WGS 84 / UTM zone 33N',
      projectionType: 'Projected (Transverse Mercator)',
      units: 'meters',
      applicableArea: 'Northern hemisphere between 12°E and 18°E',
      accuracy: '±0.5m (within zone)',
    },
  },
  'EPSG:32634': {
    proj4: '+proj=utm +zone=34 +datum=WGS84 +units=m +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:32634',
      name: 'WGS 84 / UTM zone 34N',
      projectionType: 'Projected (Transverse Mercator)',
      units: 'meters',
      applicableArea: 'Northern hemisphere between 18°E and 24°E',
      accuracy: '±0.5m (within zone)',
    },
  },
  'EPSG:32635': {
    proj4: '+proj=utm +zone=35 +datum=WGS84 +units=m +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:32635',
      name: 'WGS 84 / UTM zone 35N',
      projectionType: 'Projected (Transverse Mercator)',
      units: 'meters',
      applicableArea: 'Northern hemisphere between 24°E and 30°E',
      accuracy: '±0.5m (within zone)',
    },
  },
  'EPSG:32636': {
    proj4: '+proj=utm +zone=36 +datum=WGS84 +units=m +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:32636',
      name: 'WGS 84 / UTM zone 36N',
      projectionType: 'Projected (Transverse Mercator)',
      units: 'meters',
      applicableArea: 'Northern hemisphere between 30°E and 36°E',
      accuracy: '±0.5m (within zone)',
    },
  },
  'EPSG:32617': {
    proj4: '+proj=utm +zone=17 +datum=WGS84 +units=m +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:32617',
      name: 'WGS 84 / UTM zone 17N',
      projectionType: 'Projected (Transverse Mercator)',
      units: 'meters',
      applicableArea: 'Northern hemisphere between 84°W and 78°W',
      accuracy: '±0.5m (within zone)',
    },
  },
  'EPSG:32618': {
    proj4: '+proj=utm +zone=18 +datum=WGS84 +units=m +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:32618',
      name: 'WGS 84 / UTM zone 18N',
      projectionType: 'Projected (Transverse Mercator)',
      units: 'meters',
      applicableArea: 'Northern hemisphere between 78°W and 72°W',
      accuracy: '±0.5m (within zone)',
    },
  },
  'EPSG:32601': {
    proj4: '+proj=utm +zone=1 +datum=WGS84 +units=m +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:32601',
      name: 'WGS 84 / UTM zone 1N',
      projectionType: 'Projected (Transverse Mercator)',
      units: 'meters',
      applicableArea: 'Northern hemisphere between 180°W and 174°W',
      accuracy: '±0.5m (within zone)',
    },
  },
  // --- India & South Asia UTM Zones ---
  'EPSG:32642': {
    proj4: '+proj=utm +zone=42 +datum=WGS84 +units=m +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:32642',
      name: 'WGS 84 / UTM zone 42N (West India / Pakistan)',
      projectionType: 'Projected (UTM)',
      units: 'meters',
      applicableArea: 'Northern hemisphere between 66°E and 72°E (West India, Gujarat)',
      accuracy: '±0.5m (within zone)',
    },
  },
  'EPSG:32643': {
    proj4: '+proj=utm +zone=43 +datum=WGS84 +units=m +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:32643',
      name: 'WGS 84 / UTM zone 43N (Central/West India)',
      projectionType: 'Projected (UTM)',
      units: 'meters',
      applicableArea: 'Northern hemisphere between 72°E and 78°E (Mumbai, Delhi, Bangalore)',
      accuracy: '±0.5m (within zone)',
    },
  },
  'EPSG:32644': {
    proj4: '+proj=utm +zone=44 +datum=WGS84 +units=m +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:32644',
      name: 'WGS 84 / UTM zone 44N (East/Central India)',
      projectionType: 'Projected (UTM)',
      units: 'meters',
      applicableArea: 'Northern hemisphere between 78°E and 84°E (Hyderabad, Chennai, Kolkata)',
      accuracy: '±0.5m (within zone)',
    },
  },
  'EPSG:32645': {
    proj4: '+proj=utm +zone=45 +datum=WGS84 +units=m +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:32645',
      name: 'WGS 84 / UTM zone 45N (North-East India)',
      projectionType: 'Projected (UTM)',
      units: 'meters',
      applicableArea: 'Northern hemisphere between 84°E and 90°E (Assam, Bihar, Bangladesh)',
      accuracy: '±0.5m (within zone)',
    },
  },
  'EPSG:32646': {
    proj4: '+proj=utm +zone=46 +datum=WGS84 +units=m +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:32646',
      name: 'WGS 84 / UTM zone 46N (Far East India)',
      projectionType: 'Projected (UTM)',
      units: 'meters',
      applicableArea: 'Northern hemisphere between 90°E and 96°E (Arunachal, Nagaland)',
      accuracy: '±0.5m (within zone)',
    },
  },
  // --- European & Regional Grids ---
  'EPSG:27700': {
    proj4:
      '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +datum=OSGB36 +units=m +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:27700',
      name: 'OSGB 1936 / British National Grid',
      projectionType: 'Projected (Transverse Mercator)',
      units: 'meters',
      applicableArea: 'United Kingdom (Great Britain)',
      accuracy: '±1m',
    },
  },
  'EPSG:2154': {
    proj4:
      '+proj=lcc +lat_1=49 +lat_2=44 +lat_0=46.5 +lon_0=3 +x_0=700000 +y_0=6600000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:2154',
      name: 'RGF93 / Lambert-93',
      projectionType: 'Projected (Lambert Conformal Conic)',
      units: 'meters',
      applicableArea: 'France (mainland)',
      accuracy: '±1m',
    },
  },
  'EPSG:25832': {
    proj4: '+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:25832',
      name: 'ETRS89 / UTM zone 32N',
      projectionType: 'Projected (UTM)',
      units: 'meters',
      applicableArea: 'Europe - 6°E to 12°E (Germany, Denmark, Italy)',
      accuracy: '±0.5m',
    },
  },
  'EPSG:4269': {
    proj4: '+proj=longlat +datum=NAD83 +no_defs +type=crs',
    info: {
      epsgCode: 'EPSG:4269',
      name: 'NAD83',
      projectionType: 'Geographic',
      units: 'degrees',
      applicableArea: 'North America',
      accuracy: '±1m',
    },
  },
};

// ─── Register all CRS definitions with Proj4 ─────────────────────────────────

function registerAllCRS(): void {
  Object.entries(CRS_DEFINITIONS).forEach(([code, def]) => {
    proj4.defs(code, def.proj4);
  });
}

// Register on module load
registerAllCRS();

// ─── CRS Information ─────────────────────────────────────────────────────────

/**
 * Get information about a specific CRS by EPSG code.
 */
export function getCRSInfo(epsgCode: string): CRSInfo | null {
  const def = CRS_DEFINITIONS[epsgCode];
  return def ? { ...def.info } : null;
}

/**
 * Get the current map CRS (default is EPSG:3857 for OpenLayers).
 */
export function getCurrentMapCRS(): string {
  return 'EPSG:3857';
}

/**
 * Get all commonly supported CRS definitions.
 */
export function getCommonCRS(): CRSInfo[] {
  return Object.values(CRS_DEFINITIONS).map((def) => ({ ...def.info }));
}

/**
 * Validate whether a CRS code is supported.
 */
export function validateCRS(epsgCode: string): boolean {
  return epsgCode in CRS_DEFINITIONS;
}

/**
 * Get Proj4 string for a registered CRS code.
 */
export function getCRSProj4String(epsgCode: string): string | null {
  return CRS_DEFINITIONS[epsgCode]?.proj4 || null;
}

/**
 * Dynamically register a custom CRS code and Proj4 definition at runtime.
 */
export function registerCustomCRS(epsgCode: string, proj4String: string, info: CRSInfo): void {
  const code = epsgCode.toUpperCase().startsWith('EPSG:')
    ? epsgCode.toUpperCase()
    : `EPSG:${epsgCode.trim()}`;

  const crsEntry = {
    proj4: proj4String,
    info: {
      ...info,
      epsgCode: code,
    },
  };

  CRS_DEFINITIONS[code] = crsEntry;
  proj4.defs(code, proj4String);
}

/**
 * Check if a layer CRS differs from the map CRS.
 */
export function hasCRSMismatch(layerCRS: string | undefined, mapCRS: string): boolean {
  if (!layerCRS) return false;
  return layerCRS !== mapCRS;
}

// ─── Coordinate Transformation ───────────────────────────────────────────────

/**
 * Transform a single coordinate pair from source CRS to target CRS.
 */
export function transformCoordinates<T extends Coordinate>(
  coordinates: T,
  sourceCRS: string,
  targetCRS: string
): T {
  if (sourceCRS === targetCRS) return [...coordinates] as T;

  if (!validateCRS(sourceCRS)) {
    throw new Error(`Unsupported source CRS: ${sourceCRS}`);
  }
  if (!validateCRS(targetCRS)) {
    throw new Error(`Unsupported target CRS: ${targetCRS}`);
  }

  const result = proj4(sourceCRS, targetCRS, [coordinates[0], coordinates[1]]);
  return [result[0], result[1], ...coordinates.slice(2)] as T;
}

/**
 * Transform multiple coordinates from source CRS to target CRS, preserving Z/M ordinates.
 */
export function batchTransformCoordinates<T extends Coordinate>(
  coordinates: T[],
  sourceCRS: string,
  targetCRS: string
): T[] {
  if (sourceCRS === targetCRS) {
    return coordinates.map((coordinate) => [...coordinate] as T);
  }

  if (!validateCRS(sourceCRS)) {
    throw new Error(`Unsupported source CRS: ${sourceCRS}`);
  }
  if (!validateCRS(targetCRS)) {
    throw new Error(`Unsupported target CRS: ${targetCRS}`);
  }

  return coordinates.map((coordinate) => {
    const result = proj4(sourceCRS, targetCRS, [coordinate[0], coordinate[1]]);
    return [result[0], result[1], ...coordinate.slice(2)] as T;
  });
}

// ─── Geometry Reprojection ───────────────────────────────────────────────────

/**
 * Transform a GeoJSON geometry from source CRS to target CRS.
 * Handles Point, LineString, Polygon, MultiPoint, MultiLineString, MultiPolygon.
 */
export function transformGeometry(
  geometry: GeoJSON.Geometry,
  sourceCRS: string,
  targetCRS: string
): GeoJSON.Geometry {
  if (sourceCRS === targetCRS) {
    return JSON.parse(JSON.stringify(geometry));
  }

  switch (geometry.type) {
    case 'Point':
      return {
        type: 'Point',
        coordinates: transformCoordinates(
          geometry.coordinates as [number, number],
          sourceCRS,
          targetCRS
        ),
      };

    case 'LineString':
      return {
        type: 'LineString',
        coordinates: (geometry.coordinates as [number, number][]).map((coord) =>
          transformCoordinates(coord, sourceCRS, targetCRS)
        ),
      };

    case 'Polygon':
      return {
        type: 'Polygon',
        coordinates: (geometry.coordinates as [number, number][][]).map((ring) =>
          ring.map((coord) => transformCoordinates(coord, sourceCRS, targetCRS))
        ),
      };

    case 'MultiPoint':
      return {
        type: 'MultiPoint',
        coordinates: (geometry.coordinates as [number, number][]).map((coord) =>
          transformCoordinates(coord, sourceCRS, targetCRS)
        ),
      };

    case 'MultiLineString':
      return {
        type: 'MultiLineString',
        coordinates: (geometry.coordinates as [number, number][][]).map((line) =>
          line.map((coord) => transformCoordinates(coord, sourceCRS, targetCRS))
        ),
      };

    case 'MultiPolygon':
      return {
        type: 'MultiPolygon',
        coordinates: (geometry.coordinates as [number, number][][][]).map((polygon) =>
          polygon.map((ring) =>
            ring.map((coord) => transformCoordinates(coord, sourceCRS, targetCRS))
          )
        ),
      };

    case 'GeometryCollection':
      return {
        type: 'GeometryCollection',
        geometries: (geometry as GeoJSON.GeometryCollection).geometries.map((g) =>
          transformGeometry(g, sourceCRS, targetCRS)
        ),
      };

    default:
      throw new Error(`Unsupported geometry type: ${geometry.type}`);
  }
}

// ─── Layer Reprojection ──────────────────────────────────────────────────────

/**
 * Reproject all features in a layer to a target CRS.
 * Creates a new layer with reprojected features, preserving all attributes.
 */
export function reprojectLayer(
  features: ReprojectedFeature[],
  sourceCRS: string,
  targetCRS: string,
  layerName: string,
  geometryType: string
): ReprojectedLayer {
  if (!validateCRS(sourceCRS)) {
    throw new Error(`Unsupported source CRS: ${sourceCRS}`);
  }
  if (!validateCRS(targetCRS)) {
    throw new Error(`Unsupported target CRS: ${targetCRS}`);
  }

  const targetInfo = getCRSInfo(targetCRS);
  const suffix = targetInfo ? targetInfo.epsgCode.replace('EPSG:', '') : targetCRS;

  const reprojectedFeatures: ReprojectedFeature[] = features.map((feature) => ({
    id: feature.id,
    geometry: transformGeometry(feature.geometry, sourceCRS, targetCRS),
    properties: { ...feature.properties }, // Preserve all attributes
  }));

  return {
    name: `${layerName}_${suffix}`,
    crs: targetCRS,
    geometryType,
    features: reprojectedFeatures,
  };
}

// ─── Transformation Accuracy ─────────────────────────────────────────────────

function getDatumIdentifier(crsCode: string, info: CRSInfo): string | null {
  const definition = CRS_DEFINITIONS[crsCode]?.proj4;
  const explicitDatum = definition?.match(/(?:^|\s)\+datum=([^\s]+)/i)?.[1];
  if (explicitDatum) return explicitDatum.toUpperCase();

  const name = info.name.toUpperCase();
  if (name.includes('OSGB 1936')) return 'OSGB36';
  if (name.includes('RGF93')) return 'RGF93';
  if (name.includes('ETRS89')) return 'ETRS89';
  if (name.includes('NAD83')) return 'NAD83';
  if (name.includes('WGS 84')) return 'WGS84';
  return null;
}

/**
 * Get conservative accuracy information for a transformation between two CRS.
 * This describes registry metadata only; it does not guarantee input-data or grid-shift accuracy.
 */
export function getTransformAccuracyInfo(
  sourceCRS: string,
  targetCRS: string
): TransformAccuracyInfo {
  const sourceInfo = getCRSInfo(sourceCRS);
  const targetInfo = getCRSInfo(targetCRS);

  let estimatedAccuracy = 'Unknown; consult authoritative transformation metadata';
  let notes = 'Accuracy cannot be estimated because one or both CRS are not registered.';

  if (sourceInfo && targetInfo) {
    if (sourceCRS === targetCRS) {
      estimatedAccuracy = 'Exact (no transformation needed)';
      notes = 'Source and target CRS are identical; coordinate quality is unchanged.';
    } else {
      const sourceDatum = getDatumIdentifier(sourceCRS, sourceInfo);
      const targetDatum = getDatumIdentifier(targetCRS, targetInfo);
      const metadataSummary = `Registry accuracy metadata: source ${sourceInfo.accuracy}; target ${targetInfo.accuracy}.`;

      if (sourceDatum && targetDatum && sourceDatum === targetDatum) {
        estimatedAccuracy =
          '< 1mm numerical projection precision; practical accuracy depends on input data';
        notes = `No datum shift is indicated (${sourceDatum}). ${metadataSummary}`;
      } else if (sourceDatum && targetDatum) {
        estimatedAccuracy =
          'Variable; datum transformation accuracy is not guaranteed by this client';
        notes = `Datum transformation from ${sourceDatum} to ${targetDatum} may require authoritative parameters or grids. ${metadataSummary}`;
      } else {
        estimatedAccuracy = 'Unknown; datum relationship could not be established';
        notes = `Do not assume the source and target datums are equivalent. ${metadataSummary}`;
      }
    }
  }

  return {
    sourceCRS,
    targetCRS,
    estimatedAccuracy,
    applicableArea: targetInfo?.applicableArea || sourceInfo?.applicableArea || 'Unknown',
    notes,
  };
}

// ─── Utility ─────────────────────────────────────────────────────────────────

/**
 * Format coordinates for display based on CRS units.
 */
export function formatCoordinates(coordinates: [number, number], epsgCode: string): string {
  const info = getCRSInfo(epsgCode);
  if (!info) return `${coordinates[0].toFixed(6)}, ${coordinates[1].toFixed(6)}`;

  if (info.units === 'degrees') {
    return `${coordinates[0].toFixed(6)}°, ${coordinates[1].toFixed(6)}°`;
  }
  return `${coordinates[0].toFixed(2)}m E, ${coordinates[1].toFixed(2)}m N`;
}

/**
 * Get a short display name for a CRS.
 */
export function getCRSDisplayName(epsgCode: string): string {
  const info = getCRSInfo(epsgCode);
  return info ? `${info.epsgCode} · ${info.name}` : epsgCode;
}
